import { Check, Minus } from 'lucide-react'
import { useEffect, useRef } from 'react'

import Reveal from '@/components/motion/Reveal'
import { TOUCH_TARGET } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

interface CheckboxProps {
  /** `'mixed'` is a select-all that covers only part of what it selects. */
  checked: boolean | 'mixed'
  onCheckedChange: (next: boolean) => void
  disabled?: boolean
  'aria-label'?: string
  className?: string
}

/**
 * The 20px checkbox, including the mixed (indeterminate) state. Figma draws
 * 18 (`4243:79352`), a tie on the 4-grid that an input rounds up.
 *
 * Checked is the accent fill with the tick in the ink that reads on it, as
 * Figma draws it: a selected state is the accent's job, in either theme.
 *
 * It IS an `<input type="checkbox">`, so space toggles it, it submits with its
 * form, and a screen reader announces "checkbox, partially checked" for mixed
 * — which `role="checkbox"` on a div only gets by reimplementing all three.
 *
 * `indeterminate` is a DOM PROPERTY with no HTML attribute behind it: it cannot
 * be set from JSX at all, so it is assigned through a ref. Mixed also has to be
 * put back after a click, because the browser clears the property itself the
 * moment the user presses the box — if the caller stays mixed no prop has
 * changed, React may not re-render, and the effect below would never run.
 *
 * Clicking a mixed box reports `true`: the platform sets `checked` on the way
 * out of indeterminate, and "select the rest" is what a partially-filled
 * select-all means.
 *
 * 20px is under the 44px touch minimum, so below `lg` the label wears
 * `TOUCH_TARGET`, a 44px hit area. The label wraps the input, so a click
 * anywhere in that area lands on the control.
 */
export default function Checkbox({
  checked,
  onCheckedChange,
  disabled,
  'aria-label': ariaLabel,
  className,
}: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const mixed = checked === 'mixed'

  useEffect(() => {
    const input = inputRef.current
    if (input !== null) {
      input.indeterminate = mixed
    }
  }, [mixed])

  return (
    <label
      className={cn(
        'group relative inline-flex size-5 shrink-0 cursor-pointer touch-manipulation',
        TOUCH_TARGET,
        // 20px is also under the 24px WCAG 2.5.8 floor for a pointer, so the
        // hit area stays on at `lg`, 4px a side (28), as BackButton's does.
        'lg:before:block lg:before:-inset-1',
        'has-[:disabled]:pointer-events-none has-[:disabled]:opacity-38',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked === true}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => {
          if (mixed) {
            event.target.indeterminate = true
          }
          onCheckedChange(event.target.checked)
        }}
        className="peer size-full cursor-pointer appearance-none rounded-xs border-2 border-border-strong bg-popover transition-colors checked:border-accent checked:bg-accent indeterminate:border-accent indeterminate:bg-accent checked:group-hover:border-accent/90 checked:group-hover:bg-accent/90 indeterminate:group-hover:border-accent/90 indeterminate:group-hover:bg-accent/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      />
      {/* rest:ub — the tick pops in as it is ticked (mock `mm-scale-in`), through the kit's Reveal. */}
      {checked === true ? (
        <Reveal
          as="span"
          variant="scale-in"
          className="pointer-events-none absolute inset-0 m-auto grid size-4 place-items-center"
        >
          <Check aria-hidden="true" strokeWidth={3} className="size-4 text-accent-foreground" />
        </Reveal>
      ) : null}
      <Minus
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto size-3 text-accent-foreground opacity-0 peer-indeterminate:opacity-100"
      />
    </label>
  )
}
